import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../Utils/supabase';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkUserAuthorization = async (email) => {
        try {
            const { data, error } = await supabase
                .from('login')
                .select('email, role')
                .eq('email', email)
                .single();

            if (error || !data) {
                return null;
            }

            return data.role === 'admin' ? { ...data } : null;
        } catch (error) {
            console.error('Error checking user authorization:', error);
            return null;
        }
    };

    useEffect(() => {
        // Check active session
        const checkSession = async () => {
            try {
                const { data: { session } } = await supabase.auth.getSession();
                if (session?.user?.email) {
                    // Check if user is authorized
                    const authData = await checkUserAuthorization(session.user.email);
                    if (authData) {
                        setUser({ ...session.user, ...authData });
                    } else {
                        await supabase.auth.signOut();
                        setUser(null);
                    }
                }
                setLoading(false);
            } catch (error) {
                console.error('Session check error:', error);
                setLoading(false);
            }
        };

        checkSession();

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
            if (session?.user?.email) {
                // Check if user is authorized
                const authData = await checkUserAuthorization(session.user.email);
                if (authData) {
                    setUser({ ...session.user, ...authData });
                } else {
                    await supabase.auth.signOut();
                    setUser(null);
                    window.location.href = '/login?error=unauthorized';
                }
            } else {
                setUser(null);
            }
            setLoading(false);
        });

        return () => {
            subscription?.unsubscribe();
        };
    }, []);

    const login = async (userData) => {
        if (userData.email) {
            const authData = await checkUserAuthorization(userData.email);
            if (authData) {
                setUser({ ...userData, ...authData });
                return true;
            }
            return false;
        }
        return false;
    };

    const logout = async () => {
        try {
            await supabase.auth.signOut();
            setUser(null);
            localStorage.clear();
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    return (
        <AuthContext.Provider value={{ user, login, logout, loading }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
